/* eslint-disable @typescript-eslint/no-unused-vars */
import { Autorenew, Description, Edit, FileDownload } from "@mui/icons-material";
import { Box, Button, CircularProgress, IconButton, TableCell, Tooltip } from "@mui/material";
import { useState } from "react";
import { isMobile } from "react-device-detect";
import { toast } from "react-toastify";
import { usePractitionerRoleContext } from "../../../../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../../../../contexts/SessionContext";
import { Base64SignPDF } from "../../../../../services/dsig";
import { GetBinary } from "../../../../../services/fhir/patientsummary/Binary/GetBinary";
import { PostDocumentReference } from "../../../../../services/fhir/patientsummary/documentreference/PostDocumentReference";
import { SignatureDialog } from "../../../../medicalcare/components/prescription/SignatureDialog";
import { downloadFile } from "@/utils/fileDownloadHelper";
import {
  extractDocumentReferenceAuthorData,
  hasValidProfessionalDocumentRefs,
} from "@/utils/fhirDocumentUtils";
import { usePreferMobileCards } from "../../../context/EhrRecordsLayoutContext";
import {
  EhrMobileActionStack,
  ehrMobileActionBtnSx,
} from "../../mobile/EhrMobileActionStack";

export function CertificateActions({
  row,
  handleOpen,
  setBinaryUrl,
  setRefetch,
  cardActions,
}: any) {
  const { access, user } = useSessionContext();
  const { userPractitionerRole } = usePractitionerRoleContext();
  const isMobileCardLayout = usePreferMobileCards();

  const [originalDocument, setOriginalDocument] = useState<any>();

  const [signatureDialogOpen, setSignatureDialogOpen] =
    useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  // Extração alinhada ao contrato de AuxiliarData.DocumentReference (oid-cpf / oid-cnpj)
  const authorData = extractDocumentReferenceAuthorData(row?.fullResource);

  const objData = {
    organization: {
      id: authorData.organizationId,
      alias: authorData.organizationAlias,
      name: authorData.organizationName,
    },
    practitioner: {
      id: authorData.practitionerId,
      name: authorData.practitionerName,
    },
    servicePratice: authorData.practiceSetting,
    time: {
      start: row?.fullResource?.period?.start,
      end: row?.fullResource?.period?.end,
    },
    relatesTo: authorData.relatesTo,
    codeValue: row?.fullResource?.type?.coding?.[0]?.code,
    codeDisplay: row?.fullResource?.type?.coding?.[0]?.display,
    categoryValue: row?.fullResource?.category?.[0]?.coding?.[0]?.code,
    categoryDisplay: row?.fullResource?.category?.[0]?.coding?.[0]?.display,
  };

  const basedOn = row?.fullResource?.basedOn
    ?.map(
      (el: any) =>
        `<fhir:basedOn>
    <fhir:reference value="${el?.reference}"/>
    </fhir:basedOn>
    `
    )
    ?.join("");

  const encounter = {
    id: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Encounter"))
      ?.reference?.split("/")?.[1],

    appointmentId: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Appointment"))
      ?.reference?.split("/")?.[1],
    requester: {
      reference: userPractitionerRole?.id
        ? `PractitionerRole/${userPractitionerRole.id}`
        : undefined,
      display: authorData.practitionerName,
    },
    subject: row?.fullResource?.subject?.reference?.split("-")?.[1],
  };

  // author[2] era frágil; valida PractitionerRole + refs exigidas pelo XML
  const isDocumentOwner =
    row?.fullResource?.author?.some(
      (el: any) =>
        typeof el?.reference === "string" &&
        el.reference.includes("PractitionerRole/") &&
        el.reference.split("/")?.[1] === userPractitionerRole?.id
    ) ?? false;

  const canSign =
    access?.type === "professional" &&
    !!row?.fullResource &&
    isDocumentOwner &&
    hasValidProfessionalDocumentRefs(objData, encounter);

  /**
   * FUNÇÃO DE DOWNLOAD ATUALIZADA
   * 
   * Funciona tanto no browser quanto no React Native
   * Usa o helper downloadFile que detecta o ambiente automaticamente
   */
  async function downloadBase64File() {
    try {
      console.log('[CertificateActions] Baixando documento...');
      
      const response = await GetBinary({
        urlBinary: row?.binaryUrl,
      });
      
      // Usa o helper que funciona em ambos os ambientes (browser + React Native)
      downloadFile(
        response?.data,           // Base64 do arquivo
        'Relatorio.pdf',         // Nome do arquivo
        response?.contentType     // Tipo MIME
      );
      
      console.log('[CertificateActions] Download concluído!');
    } catch (err) {
      console.error('[CertificateActions] Erro ao baixar:', err);
      toast.error('Erro ao baixar documento.');
    }
  }

  const handleSignature = async (
    provider: "vidaas" | "safeid",
    password?: string,
    lifeTime?: number
  ) => {
    // Não envia PostDocumentReference com payload incompleto (evita author=Patient)
    if (!canSign) {
      toast.warn(
        "Dados do profissional/organização incompletos. Aguarde o carregamento e tente novamente."
      );
      return;
    }

    try {
      setLoading(true);
      toast.warn("Aguardando verificação e segurança do seu provedor!");
      const signResponse = await Base64SignPDF({
        provider: provider,
        ...(provider === "safeid" && {
          userPassword: password,
        }),
        pdfBase64: originalDocument,
        cpfCnpj: user?.username,
        lifeTime: lifeTime ?? 0,
      });

      if (!signResponse?.length) throw new Error("");

      await PostDocumentReference(
        { ...objData, base64: signResponse },
        encounter,
        basedOn,
        true
      );
      toast.success("Assinatura realizada!");
    } catch (error) {
      toast.warn(
        "Não foi possível realizar a assinatura, por favor tente novamente!"
      );
      console.error("HandleSignature error:", error);
    } finally {
      setLoading(false);
      setRefetch((prev: boolean) => !prev);
    }
  };
  const signatureDialog = signatureDialogOpen && originalDocument ? (
    <SignatureDialog
      visualize={originalDocument}
      isOpen={signatureDialogOpen}
      handleClose={() => {
        setSignatureDialogOpen(false);
      }}
      handleSignature={handleSignature}
      row={{ sign: "Pendente" }}
      loading={loading}
    />
  ) : null;

  if (isMobileCardLayout) {
    return (
      <>
        <EhrMobileActionStack>
          {!cardActions ? (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<Description />}
              sx={ehrMobileActionBtnSx}
              onClick={() => {
                setBinaryUrl(row.binaryUrl);
                handleOpen();
              }}
            >
              Visualizar documento
            </Button>
          ) : null}

          <Button
            fullWidth
            variant={cardActions ? "outlined" : "outlined"}
            color="primary"
            startIcon={<FileDownload />}
            sx={ehrMobileActionBtnSx}
            onClick={() => {
              downloadBase64File();
            }}
          >
            {cardActions ? "Documento assinado" : "Baixar documento"}
          </Button>

          {canSign ? (
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={
                loading ? (
                  <CircularProgress size={18} />
                ) : !row?.sign ? (
                  <Edit />
                ) : (
                  <Autorenew />
                )
              }
              sx={ehrMobileActionBtnSx}
              disabled={loading}
              onClick={async () => {
                const response = await GetBinary({
                  urlBinary: row?.binaryUrl,
                });
                setOriginalDocument(response?.data);
                setSignatureDialogOpen(true);
              }}
            >
              {!row?.sign ? "Assinar documento" : "Reemitir documento"}
            </Button>
          ) : null}
        </EhrMobileActionStack>

        {signatureDialog}
      </>
    );
  }

  return (
    <>
      {cardActions ? (
        <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
          <Button
            onClick={() => {
              downloadBase64File();
            }}
            startIcon={<FileDownload />}
            variant="outlined"
            color="primary"
          >
            Documento assinado
          </Button>
        </Box>
      ) : (
        <TableCell align="center" sx={{ minWidth: "120px" }}>
          {!isMobile && (
            <Tooltip title="Visualizar documento">
              <IconButton
                onClick={() => {
                  setBinaryUrl(row.binaryUrl);
                  handleOpen();
                }}
              >
                <Description />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Baixar documento">
            <IconButton
              onClick={() => {
                downloadBase64File();
              }}
            >
              <FileDownload />
            </IconButton>
          </Tooltip>

          {canSign && (
          <Tooltip
            title={!row?.sign ? "Assinar documento" : "Reemitir documento"}
          >
            <IconButton
              onClick={async () => {
                const response = await GetBinary({
                  urlBinary: row?.binaryUrl,
                });
                setOriginalDocument(response?.data);
                setSignatureDialogOpen(true);
              }}
            >
              {loading ? (
                <CircularProgress size={20} />
              ) : !row?.sign ? (
                <Edit />
              ) : (
                <Autorenew />
              )}
            </IconButton>
          </Tooltip>
        )}
        </TableCell>
      )}

      {signatureDialog}
    </>
  );
}
